import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { getUsers, createUser, updateUser, deleteUser, getBranches, changeUserPassword, setUserActive } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users as UsersIcon, Shield, User, Building2, Key, UserCheck, UserX } from 'lucide-react';
import { Switch } from '../components/ui/switch';

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'vendedor',
    branch_id: '',
    requiere_caja: true,
  });
  const [loading, setLoading] = useState(true);
  const [passwordModal, setPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersData, branchesData] = await Promise.all([
        getUsers(),
        getBranches()
      ]);
      setUsers(usersData);
      setBranches(branchesData);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await updateUser(editingUser.id, {
          name: formData.name,
          role: formData.role,
          branch_id: formData.branch_id || null,
          requiere_caja: formData.requiere_caja !== false,
        });
        toast.success('Usuario actualizado');
      } else {
        await createUser(formData);
        toast.success('Usuario creado exitosamente');
      }
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar usuario');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name,
      role: user.role,
      branch_id: user.branch_id || '',
      requiere_caja: user.requiere_caja !== false,
    });
    setIsDialogOpen(true);
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres'); return;
    }
    setSavingPassword(true);
    try {
      await changeUserPassword(passwordModal.id, newPassword);
      toast.success(`Contraseña de ${passwordModal.name} actualizada`);
      setPasswordModal(null);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error actualizando contraseña');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await setUserActive(user.id, user.is_active === false);
      toast.success(user.is_active === false ? `${user.name} activado` : `${user.name} desactivado`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error actualizando usuario');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este usuario?')) {
      try {
        await deleteUser(id);
        toast.success('Usuario eliminado');
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Error al eliminar usuario');
      }
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'vendedor',
      branch_id: ''
    });
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { label: 'Administrador', className: 'bg-purple-100 text-purple-800', icon: Shield },
      vendedor: { label: 'Vendedor', className: 'bg-green-100 text-green-800', icon: User }
    };
    const config = roleConfig[role] || roleConfig.vendedor;
    const Icon = config.icon;
  return (
      <Badge className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getBranchName = (branchId) => {
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : 'Sin asignar';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Cargando...</div>;
  }

  return (
    <>
    <div className="p-6 md:p-8 space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>Usuarios del Sistema</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={resetForm}
              className="bg-cyan-500 hover:bg-cyan-600 text-white shadow-md shadow-cyan-500/20"
              data-testid="add-user-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nuevo Usuario
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre Completo *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="user-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={editingUser}
                  data-testid="user-email-input"
                />
                {editingUser && (
                  <p className="text-xs text-slate-500">El email no se puede modificar</p>
                )}
              </div>

              {!editingUser && (
                <div className="space-y-2">
                  <Label>Contraseña *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                    placeholder="Mínimo 6 caracteres"
                    data-testid="user-password-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Rol *</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="vendedor">Vendedor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Sucursal</Label>
                <Select 
                  value={formData.branch_id || "none"} 
                  onValueChange={(value) => setFormData({ ...formData, branch_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="user-branch-select">
                    <SelectValue placeholder="Seleccionar sucursal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Los usuarios solo verán datos de su sucursal asignada (excepto administradores)
                </p>
              </div>

              <div className="bg-slate-50 p-3 rounded-lg text-xs text-slate-600 space-y-1">
                <p><strong>Administrador:</strong> Acceso completo, todas las sucursales</p>
                <p><strong>Cajero:</strong> Facturación y ventas de su sucursal</p>
                <p><strong>Vendedor:</strong> Cotizaciones y clientes de su sucursal</p>
              </div>

              {formData.role !== 'admin' && (
                <div className="flex items-center justify-between border border-slate-200 rounded-lg px-4 py-3">
                  <div>
                    <p className="text-sm font-medium">Requiere turno de caja</p>
                    <p className="text-xs text-slate-500">Debe tener caja abierta para operar</p>
                  </div>
                  <Switch
                    checked={formData.requiere_caja !== false}
                    onCheckedChange={(v) => setFormData({ ...formData, requiere_caja: v })}
                  />
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-cyan-500 hover:bg-cyan-600" data-testid="save-user-button">
                  {editingUser ? 'Actualizar' : 'Crear'} Usuario
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} data-testid="cancel-user-button">
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="users-grid">
        {users.map(user => (
          <Card key={user.id} className={`border shadow-sm hover:shadow-md transition-shadow duration-200 ${user.is_active === false ? 'bg-slate-50 border-slate-200 opacity-70' : 'bg-white border-slate-200'}`} data-testid={`user-card-${user.id}`}>
            <CardHeader>
              <CardTitle className="flex items-start justify-between" style={{ fontFamily: 'Outfit' }}>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl">{user.name}</h3>
                    {user.is_active === false && (
                      <span className="text-xs bg-red-100 text-red-700 rounded-full px-2 py-0.5 font-medium">Inactivo</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 font-normal mt-1">{user.email}</p>
                </div>
                <UsersIcon className="w-5 h-5 text-cyan-500" />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Rol:</span>
                {getRoleBadge(user.role)}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Sucursal:</span>
                <span className="flex items-center text-sm">
                  <Building2 className="w-3 h-3 mr-1 text-slate-400" />
                  {getBranchName(user.branch_id)}
                </span>
                {user.role !== 'admin' && user.requiere_caja === false && (
                  <span className="text-xs bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">Sin caja obligatoria</span>
                )}
              </div>

              {user.created_at && (
                <div className="text-xs text-slate-500">
                  Creado: {new Date(user.created_at).toLocaleDateString('es-DO')}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" title="Cambiar contraseña"
                  onClick={() => { setPasswordModal(user); setNewPassword(''); }}>
                  <Key className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  title={user.is_active === false ? 'Activar usuario' : 'Desactivar usuario'}
                  onClick={() => handleToggleActive(user)}
                  disabled={user.id === currentUser?.id || (user.is_active !== false && user.role === 'admin' && users.filter(u => u.role === 'admin' && u.is_active !== false).length <= 1)}
                  className={user.is_active === false ? 'text-green-600 hover:bg-green-50 hover:text-green-700' : 'text-amber-500 hover:bg-amber-50 hover:text-amber-700'}
                >
                  {user.is_active === false ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleEdit(user)}
                  data-testid={`edit-user-${user.id}`}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Button>
                <Button
                  variant="outline"
                  className="text-rose-500 hover:bg-rose-50 hover:text-rose-700"
                  onClick={() => handleDelete(user.id)}
                  disabled={user.id === currentUser?.id || (user.role === 'admin' && users.filter(u => u.role === 'admin').length === 1)}
                  data-testid={`delete-user-${user.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {user.role === 'admin' && users.filter(u => u.role === 'admin').length === 1 && (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  No se puede eliminar el último administrador
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {users.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          No hay usuarios registrados
        </div>
      )}
    </div>

      {/* Password Modal */}
      <Dialog open={!!passwordModal} onOpenChange={() => setPasswordModal(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Cambiar contraseña</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Cambiando contraseña de <strong>{passwordModal?.name}</strong>
            </p>
            <div className="space-y-2">
              <Label>Nueva contraseña</Label>
              <Input type="password" value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleChangePassword()}
                placeholder="Mínimo 6 caracteres" autoFocus />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPasswordModal(null)}>Cancelar</Button>
            <Button onClick={handleChangePassword} disabled={savingPassword}
              className="bg-slate-700 hover:bg-slate-800 text-white">
              <Key className="w-4 h-4 mr-2" />
              {savingPassword ? 'Guardando...' : 'Cambiar contraseña'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Users;